// src/components/calculator/AutonomoSummaryBlock.js
//
// Ticket 069 (D8). MOVED unchanged from src/pages/AutonomoCalculatorPage.js: a pure
// presentational block (RETA + Modelo 130 + activity take-home) shared by the
// Autónomo calculator result, the saved-result page and the Advisor Intake results
// page. AutonomoCalculatorPage re-exports it, so existing importers keep working.
//
// RULE-2 EXCEPTION (called out in the plan): the two `style={{ width / left }}` on the
// RETA range bar are dynamic percentages and moved here as they were.

import React from 'react'

export function fmtEur(amount) {
  if (amount === null || amount === undefined) return '—'
  return '€' + Math.round(amount).toLocaleString('en')
}

// Exported so CalculatorSavedPage.js can render the same block on reload —
// not duplicated there.
export function hasPersonalFactsFromSession(session) {
  return !!(session?.profile_facts?.comunidad && session?.tax_year_facts?.residency_year_context)
}

export function AutonomoSummaryBlock({ autonomoResult, autonomoIrpfImpact, result, hasPersonalFacts }) {
  const reta = autonomoResult?.reta
  const modelo130 = autonomoResult?.modelo130
  const tarifaPlana = autonomoResult?.tarifaPlana

  // Revenue/expenses/net profit are attributable to the activity itself —
  // reading them from result.classified_items (server-normalized) rather
  // than raw session items, since currency/period conversion already
  // happened there. Filtered to self-employment, self-owned items only —
  // a spouse's own self-employment activity isn't modeled by this
  // calculator (see OTHER_INCOME_CARDS excluding self_employment).
  const selfEmploymentItems = (result?.classified_items || [])
    .filter(i => i.income_type === 'self_employment' && (i.owner || 'self') !== 'spouse')
  const revenue  = selfEmploymentItems.reduce((sum, i) => sum + (i.gross_amount_eur ?? 0), 0)
  const netAfterExpenses = selfEmploymentItems.reduce((sum, i) => sum + (i.net_amount_eur ?? 0), 0)
  const expenses = revenue - netAfterExpenses
  // effectiveMonthlyReta is null when there is no self-employment income
  // (engine decides; ticket 042). The UI keys off null, not its own income check.
  const hasRetaFigure = autonomoResult?.effectiveMonthlyReta != null
  const retaAnnual = hasRetaFigure ? autonomoResult.effectiveMonthlyReta * 12 : 0
  const irpfImpact = autonomoIrpfImpact?.incremental_irpf ?? 0
  // Take-home = net profit minus RETA minus this activity's IRPF impact.
  // Falls back to net-minus-RETA if autonomoIrpfImpact wasn't computed
  // (it's a non-fatal engine call and can be absent).
  const takeHome = netAfterExpenses - retaAnnual - irpfImpact
  const hasBusinessFigures = selfEmploymentItems.length > 0

  // RETA band range visual — band.base_min/base_max come straight off
  // RetaBand (autonomoCuota.ts), same object already used for band_label
  // below, so no engine change needed for this.
  const band = reta?.band
  const rangePct = band && band.base_max > band.base_min
    ? Math.max(0, Math.min(100, ((reta.selected_base - band.base_min) / (band.base_max - band.base_min)) * 100))
    : 0

  return (
    <div className="cal-inputs-summary">
      <div className="cal-inputs-summary-title">Your autónomo numbers</div>

      {!hasPersonalFacts && (
        <p className="cal-eligibility-alert-reason">
          The Spanish income tax figure below now includes your comunidad, residency,
          and family details — but if you skipped any of those questions, treat it as
          a rough estimate.
        </p>
      )}

      {/* 3-stat row: RETA / Modelo 130 / take-home. "Recommended filing"
          deliberately left out — it's conditional (married + spouse
          income only) while these three apply to every autónomo session,
          and the filing recommendation already appears once, in the IRPF
          hero below, via its own "Recommended" stat. */}
      {(reta || modelo130) && (
        <div className="cal-autonomo-stat-row">
          {reta && hasRetaFigure && (
            <div className="cal-autonomo-stat">
              <div className="cal-hero-eyebrow">Monthly RETA</div>
              <div className="asmt-irpf-metric-value">{fmtEur(autonomoResult.effectiveMonthlyReta)}/mo</div>
              <div className="u-hint">Estimated Social Security contribution</div>
            </div>
          )}
          {modelo130 && modelo130.perActivity.length > 0 && (
            <div className="cal-autonomo-stat">
              <div className="cal-hero-eyebrow">Modelo 130</div>
              <div className="asmt-irpf-metric-value">{fmtEur(modelo130.totalEstimatedQuarterlyPayment)}/quarter</div>
              <div className="u-hint">Estimated quarterly payment</div>
            </div>
          )}
          {hasBusinessFigures && (
            <div className="cal-autonomo-stat">
              <div className="cal-hero-eyebrow">Estimated activity take-home</div>
              <div className="asmt-irpf-metric-value cal-autonomo-stat-value--teal">{fmtEur(takeHome)}</div>
              <div className="u-hint">Based on business profit, RETA, and the estimated tax effect of this activity</div>
            </div>
          )}
        </div>
      )}

      {/* Business economics — attributable to the activity itself,
          regardless of filing method or marital status. */}
      {hasBusinessFigures && (
        <div className="cal-business-card">
          <div className="cal-business-card-header">
            <span className="cal-icon-chip"><i className="ti ti-briefcase" aria-hidden="true" /></span>
            <div className="cal-business-card-title">Your autónomo activity</div>
          </div>

          <div className="cal-business-grid">
            <div className="cal-business-stack">
              <div className="cal-business-row">
                <span>Revenue</span>
                <span className="cal-business-row-value">{fmtEur(revenue)}</span>
              </div>
              <div className="cal-business-row cal-business-row--out">
                <span>Deductible expenses</span>
                <span className="cal-business-row-value">−{fmtEur(expenses)}</span>
              </div>
              <div className="cal-business-row">
                <span>Net business profit</span>
                <span className="cal-business-row-value">{fmtEur(netAfterExpenses)}</span>
              </div>
              {hasRetaFigure && (
                <div className="cal-business-row cal-business-row--out">
                  <span>RETA (annualized)</span>
                  <span className="cal-business-row-value">−{fmtEur(retaAnnual)}</span>
                </div>
              )}
              {autonomoIrpfImpact && (
                <div className="cal-business-row cal-business-row--out">
                  <span>Estimated IRPF impact</span>
                  <span className="cal-business-row-value">−{fmtEur(irpfImpact)}</span>
                </div>
              )}
              <div className="cal-business-row cal-business-row--total">
                <span>Estimated activity take-home</span>
                <span className="cal-business-row-value">{fmtEur(takeHome)}</span>
              </div>
            </div>

            <div className="cal-business-info">
              {band && (
                <div className="cal-business-info-card">
                  <div className="cal-business-info-card-label">
                    <i className="ti ti-shield-check" aria-hidden="true" /> RETA band
                  </div>
                  <div className="cal-business-info-card-value">{band.band_label}</div>
                  <div className="cal-reta-range">
                    <div className="cal-reta-range-track">
                      <div className="cal-reta-range-fill" style={{ width: `${rangePct}%` }} />
                      <div className="cal-reta-range-marker" style={{ left: `${rangePct}%` }} />
                    </div>
                    <div className="cal-reta-range-labels">
                      <span>{fmtEur(band.base_min)}</span>
                      <span>{fmtEur(band.base_max)}</span>
                    </div>
                  </div>
                  {tarifaPlana?.status === 'eligible' && hasRetaFigure && (
                    <p className="cal-income-empty-hint">Flat-rate (tarifa plana) applied — {tarifaPlana.reason}</p>
                  )}
                  {tarifaPlana?.status === 'uncertain' && (
                    <p className="cal-income-empty-hint">Flat-rate eligibility uncertain — {tarifaPlana.reason}</p>
                  )}
                </div>
              )}
              {reta && (
                <div className="cal-business-info-card">
                  <div className="cal-business-info-card-label">
                    <i className="ti ti-user" aria-hidden="true" /> Contribution base used
                  </div>
                  <div className="cal-business-info-card-value">{fmtEur(reta.selected_base)}</div>
                </div>
              )}
              <p className="u-hint">
                Your spouse's income can affect your IRPF impact, but it does not affect your
                RETA or Modelo 130 payments.
              </p>
            </div>
          </div>

          {(modelo130?.sessionAssumptions?.length > 0 || autonomoIrpfImpact) && (
            <div className="cal-assumptions-used">
              <div className="cal-assumptions-used-label">Assumptions used</div>
              <ul className="cal-assumptions-used-list">
                {modelo130?.sessionAssumptions?.map((note, i) => (
                  <li key={`m130-${i}`}>{note}</li>
                ))}
                {autonomoIrpfImpact && <li>{autonomoIrpfImpact.note}</li>}
                <li>
                  Spanish income tax depends on your other income and, if married, whether you
                  file individually or jointly — see below for that estimate.
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
